import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Info } from "lucide-react";
import LocationsEditor from "@/components/LocationsEditor";

const PROFESSION_LABELS = {
  medico_medicina_generale: "Medico di Medicina Generale",
  medico_specialista: "Medico Specialista",
  laboratorio: "Laboratorio di Analisi",
  diagnostica: "Struttura Diagnostica",
  biologo_nutrizionista: "Biologo Nutrizionista",
  infermiere: "Infermiere",
  psicologo: "Psicologo",
  fisioterapista: "Fisioterapista",
  logopedista: "Logopedista",
  podologo: "Podologo",
  altro_professionista: "Altro Professionista Sanitario",
};

const SPECIALIZATIONS = [
  "Medicina Generale",
  "Cardiologia",
  "Ginecologia e Ostetricia",
  "Ortopedia e Traumatologia",
  "Dermatologia",
  "Urologia",
  "Neurologia",
  "Pneumologia",
  "Gastroenterologia",
  "Endocrinologia",
  "Reumatologia",
  "Otorinolaringoiatria",
  "Oculistica",
  "Pediatria",
  "Psichiatria",
  "Nutrizione (biologo)",
  "Medicina Interna",
  "Angiologia",
  "Nefrologia",
  "Ematologia",
  "Oncologia",
  "Radiologia",
  "Anestesiologia",
  "Chirurgia Generale",
  "Neurochirurgia",
  "Chirurgia Vascolare",
  "Fisiatria e Riabilitazione",
  "Medicina del Lavoro",
  "Medicina dello Sport",
  "Igiene e Medicina Preventiva",
  "Altra specializzazione",
];

const IT_PROVINCES = [
  "Agrigento","Alessandria","Ancona","Aosta","Arezzo","Ascoli Piceno","Asti","Avellino",
  "Bari","Barletta-Andria-Trani","Belluno","Benevento","Bergamo","Biella","Bologna","Bolzano","Brescia","Brindisi",
  "Cagliari","Caltanissetta","Campobasso","Caserta","Catania","Catanzaro","Chieti","Como","Cosenza","Cremona","Crotone","Cuneo",
  "Enna","Fermo","Ferrara","Firenze","Foggia","Forlì-Cesena","Frosinone",
  "Genova","Gorizia","Grosseto","Imperia","Isernia","La Spezia","L'Aquila","Latina","Lecce","Lecco","Livorno","Lodi","Lucca",
  "Macerata","Mantova","Massa-Carrara","Matera","Messina","Milano","Modena","Monza e Brianza",
  "Napoli","Novara","Nuoro","Oristano","Padova","Palermo","Parma","Pavia","Perugia","Pesaro e Urbino","Pescara","Piacenza","Pisa","Pistoia","Pordenone","Potenza","Prato",
  "Ragusa","Ravenna","Reggio Calabria","Reggio Emilia","Rieti","Rimini","Roma","Rovigo",
  "Salerno","Sassari","Savona","Siena","Siracusa","Sondrio","Sud Sardegna",
  "Taranto","Teramo","Terni","Torino","Trapani","Trento","Treviso","Trieste","Udine",
  "Varese","Venezia","Verbano-Cusio-Ossola","Vercelli","Verona","Vibo Valentia","Vicenza","Viterbo",
];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", company_name: "", fiscal_code: "", birth_date: "",
    email: "", phone: "", password: "",
    profession: "medico_specialista",
    specialization: "",
    academic_title: "",
    order_province: "Roma", order_number: "",
  });
  const [locations, setLocations] = useState([{ name: "", city: "", address: "" }]);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isOrg = form.profession === "laboratorio" || form.profession === "diagnostica";

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { setError("Devi accettare l'informativa privacy."); return; }
    if (isOrg && !form.company_name.trim()) {
      setError("Per Laboratorio o Struttura Diagnostica la Ragione Sociale è obbligatoria.");
      return;
    }
    if (!isOrg && (!form.first_name.trim() || !form.last_name.trim())) {
      setError("Nome e Cognome sono obbligatori.");
      return;
    }
    setError(""); setLoading(true);
    const cleanLocations = (locations || [])
      .map((l) => ({ name: (l.name || "").trim(), city: (l.city || "").trim(), address: (l.address || "").trim() }))
      .filter((l) => l.name && l.city);
    const payload = { ...form, fiscal_code: form.fiscal_code.toUpperCase(),
      locations: cleanLocations,
      privacy_consent: true, privacy_consent_version: "1.0" };
    const res = await register(payload);
    setLoading(false);
    if (res.ok) {
      toast.success("Registrazione ricevuta");
      navigate("/verifica-in-corso", { state: { justRegistered: true, email: form.email } });
    } else {
      setError(res.error || "Errore registrazione");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 md:p-8">
      <form onSubmit={submit}
            className="w-full max-w-3xl bg-white rounded-2xl border border-slate-200 p-6 md:p-10 shadow-sm">
        <div className="flex items-center justify-center mb-6">
          <img src="/logo.svg?v=20" alt="Synappsi" className="h-14 w-auto" />
        </div>
        <h2 className="font-heading text-3xl font-extrabold text-slate-900 text-center">Registrazione Professionista</h2>
        <p className="text-sm text-slate-500 mt-2 text-center max-w-lg mx-auto">
          Richiedi l'accesso a Synappsi. L'account verrà attivato solo dopo verifica da parte degli amministratori.
        </p>

        <div className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-[#DBEAFE]/50 border border-[#1E3A8A]/20">
          <ShieldCheck className="h-4 w-4 text-[#1E3A8A] mt-0.5 flex-shrink-0" />
          <div className="text-xs text-slate-600 leading-relaxed">
            Dati anagrafici e di iscrizione all'Ordine sono richiesti per verificare l'identità professionale. Non sarai attivo finché un amministratore non completa la verifica.
          </div>
        </div>

        <div className="mt-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Anagrafica</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>
                Ragione Sociale {isOrg ? <span className="text-red-600">*</span> : <span className="text-slate-400 font-normal">(facoltativa)</span>}
              </Label>
              <Input value={form.company_name} onChange={upd("company_name")}
                     required={isOrg}
                     placeholder={isOrg ? "Es. Laboratorio Analisi Cliniche San Marco S.r.l." : "Studio Medico Dr. Rossi (facoltativa)"}
                     data-testid="reg-company-name" />
              <p className="text-[11px] text-slate-500 mt-1">
                {isOrg
                  ? "Denominazione ufficiale della struttura. Sarà mostrata nella rubrica e nelle richieste."
                  : "Se hai uno studio o operi con una denominazione, indicala qui. Non è obbligatoria."}
              </p>
            </div>
            {isOrg ? (
              <>
                <div><Label>Referente — Nome <span className="text-slate-400 font-normal">(opzionale)</span></Label>
                  <Input value={form.first_name} onChange={upd("first_name")} data-testid="reg-first-name" />
                </div>
                <div><Label>Referente — Cognome <span className="text-slate-400 font-normal">(opzionale)</span></Label>
                  <Input value={form.last_name} onChange={upd("last_name")} data-testid="reg-last-name" />
                </div>
              </>
            ) : (
              <>
                <div><Label>Nome</Label><Input value={form.first_name} onChange={upd("first_name")} required data-testid="reg-first-name" /></div>
                <div><Label>Cognome</Label><Input value={form.last_name} onChange={upd("last_name")} required data-testid="reg-last-name" /></div>
              </>
            )}
            <div><Label>{isOrg ? "Codice fiscale / P.IVA" : "Codice fiscale"}</Label>
              <Input value={form.fiscal_code} onChange={upd("fiscal_code")} required minLength={11} maxLength={16}
                     className="uppercase font-mono" placeholder={isOrg ? "12345678901" : "RSSMRA80A01H501U"} data-testid="reg-fiscal" />
            </div>
            <div><Label>{isOrg ? "Data di costituzione / Iscrizione" : "Data di nascita"}</Label>
              <Input type="date" value={form.birth_date} onChange={upd("birth_date")} required data-testid="reg-birth" />
            </div>
          </div>
        </div>

        <div className="mt-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Contatti</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Email professionale</Label>
              <Input type="email" value={form.email} onChange={upd("email")} required data-testid="reg-email" />
            </div>
            <div><Label>Numero di telefono</Label>
              <Input value={form.phone} onChange={upd("phone")} required placeholder="+39 …" data-testid="reg-phone" />
            </div>
            <div className="md:col-span-2"><Label>Password (min. 8 caratteri)</Label>
              <Input type="password" value={form.password} onChange={upd("password")} required minLength={8}
                     data-testid="reg-password" placeholder="••••••••" />
            </div>
          </div>
        </div>

        <div className="mt-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Iscrizione all'Ordine professionale</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Professione</Label>
              <Select value={form.profession} onValueChange={(v) => setForm({ ...form, profession: v })}>
                <SelectTrigger data-testid="reg-profession"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PROFESSION_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Specializzazione {form.profession === "medico_specialista" && <span className="text-red-500">*</span>}</Label>
              <Select value={form.specialization}
                      onValueChange={(v) => setForm({ ...form, specialization: v })}>
                <SelectTrigger data-testid="reg-specialization">
                  <SelectValue placeholder="Seleziona la tua specializzazione" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {SPECIALIZATIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                In base alla specializzazione vedrai solo i moduli sintomo pertinenti alla tua pratica clinica.
              </p>
            </div>
            <div className="md:col-span-2"><Label>Titolo di studio / qualifica <span className="text-slate-400 text-xs">(facoltativo)</span></Label>
              <Input value={form.academic_title} onChange={upd("academic_title")}
                     placeholder="Es. Specialista in Cardiologia · Ph.D. Medicina Interna · Prof. Associato"
                     maxLength={120}
                     data-testid="reg-academic-title" />
              <p className="text-[11px] text-slate-500 mt-1">
                Verrà mostrato accanto al tuo nome in rubrica per far riconoscere subito le tue qualifiche ai colleghi.
              </p>
            </div>
            <div><Label>Provincia dell'Ordine</Label>
              <Select value={form.order_province} onValueChange={(v) => setForm({ ...form, order_province: v })}>
                <SelectTrigger data-testid="reg-province"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {IT_PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Numero di iscrizione</Label>
              <Input value={form.order_number} onChange={upd("order_number")} required
                     placeholder="Es. 12345" data-testid="reg-order-number" />
            </div>
          </div>
        </div>

        <div className="mt-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Sede / sedi di lavoro</p>
          <p className="text-xs text-slate-500 mb-3">
            Indica dove ricevi/lavori. Se visiti in più strutture (poliambulatori, sedi distaccate), spunta <em>più sedi</em>: i colleghi ti troveranno per città.
          </p>
          <LocationsEditor value={locations} onChange={setLocations} testidPrefix="reg-loc" required={false} />
        </div>

        {error && (
          <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3"
               data-testid="reg-error">{error}</div>
        )}

        <div className="mt-6 flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <Checkbox id="consent" checked={consent} onCheckedChange={setConsent}
                    data-testid="reg-consent" className="mt-0.5" />
          <label htmlFor="consent" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
            Ho letto e accetto l'
            <Link to="/privacy" target="_blank" className="text-[#1E3A8A] font-semibold underline" data-testid="link-privacy-reg">informativa privacy</Link>{" "}
            e presto consenso al trattamento dei dati (inclusi dati sanitari ex art. 9 GDPR) per le finalità di comunicazione clinica descritte. Confermo di essere un professionista sanitario autorizzato e che i dati inseriti sono veritieri.
          </label>
        </div>

        <Button type="submit" disabled={loading || !consent} data-testid="reg-submit"
                className="mt-4 w-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11">
          {loading ? "Invio…" : "Invia richiesta di verifica"}
        </Button>

        <div className="mt-4 text-sm text-slate-500 text-center">
          Hai già un account?{" "}
          <Link to="/login" className="text-[#1E3A8A] font-semibold hover:underline" data-testid="link-login">Accedi</Link>
        </div>
      </form>
    </div>
  );
}
